import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { Field, Input, Select, Textarea, FormGrid, SectionLabel, SaveMessage, Card, CardHeader, SubmitBtn, EmptyState, RecordMeta, Badge } from './Shared';

const ALL_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];
const TYPES = [
  { value: 'city', label: 'City' }, { value: 'county', label: 'County' }, { value: 'state', label: 'State' },
  { value: 'district', label: 'Fire district' }, { value: 'other', label: 'Other' }
];
const STATUSES = [
  { value: 'unknown', label: 'Unknown', variant: 'gray' },
  { value: 'none', label: 'No requirement', variant: 'gray' },
  { value: 'policy_only', label: 'Policy only', variant: 'amber' },
  { value: 'recognizes_certification', label: 'Recognizes certification', variant: 'blue' },
  { value: 'requires_certification', label: 'Requires certification', variant: 'green' },
  { value: 'requires_validation', label: 'Requires validation (Judgr)', variant: 'purple' }
];
const STATUS_BY_VALUE = Object.fromEntries(STATUSES.map(s => [s.value, s]));
const today = () => new Date().toLocaleDateString('en-CA');
const fmtDate = d => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'never';

const emptyForm = () => ({
  name: '', state: '', county: '', jurisdiction_type: 'city', fire_marshal_name: '', contact_email: '', contact_phone: '',
  requirement_status: 'unknown', source_url: '', last_verified: '', notes: ''
});

export default function JurisdictionsPage({ profile }) {
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [filterState, setFilterState] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [msg, setMsg] = useState({ show: false, text: '', error: false });

  useEffect(() => { load(); }, []);

  async function load() {
    const { data, error } = await supabase.from('jurisdictions').select('*').order('state').order('name');
    setLoadError(error ? error.message : '');
    setRecords(data || []);
  }

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const clean = v => (v && v.trim()) || null;

  async function save() {
    if (!form.name.trim()) { setMsg({ show: true, text: 'Jurisdiction name required.', error: true }); return; }
    if (!form.state) { setMsg({ show: true, text: 'Pick a state.', error: true }); return; }
    setLoading(true);
    const { error } = await supabase.from('jurisdictions').insert({
      name: form.name.trim(),
      state: form.state,
      county: clean(form.county),
      jurisdiction_type: form.jurisdiction_type,
      fire_marshal_name: clean(form.fire_marshal_name),
      contact_email: clean(form.contact_email),
      contact_phone: clean(form.contact_phone),
      requirement_status: form.requirement_status,
      source_url: clean(form.source_url),
      last_verified: form.last_verified || null,
      notes: clean(form.notes),
      created_by: profile.id
    });
    setLoading(false);
    if (error) { setMsg({ show: true, text: 'Error: ' + error.message, error: true }); return; }
    setMsg({ show: true, text: 'Jurisdiction added.', error: false });
    setForm(emptyForm());
    load();
    setTimeout(() => setMsg({ show: false }), 3000);
  }

  async function updateStatus(id, status) {
    const { error } = await supabase.from('jurisdictions')
      .update({ requirement_status: status, last_verified: today(), updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) { setMsg({ show: true, text: 'Error: ' + error.message, error: true }); return; }
    load();
  }

  const counts = {};
  records.forEach(r => { counts[r.requirement_status] = (counts[r.requirement_status] || 0) + 1; });
  const statesCovered = new Set(records.map(r => r.state)).size;
  const shown = records.filter(r => (!filterState || r.state === filterState) && (!filterStatus || r.requirement_status === filterStatus));

  return (
    <div>
      <div style={{ fontSize: '13px', color: '#64748B', marginBottom: '16px', lineHeight: '1.6' }}>
        Where each fire marshal / AHJ stands on certified, documented fire watch. Changing a status stamps today as the last-verified date.
      </div>

      {loadError && (
        <Card>
          <div style={{ color: '#B91C1C', fontSize: '13px' }}>
            Could not load jurisdictions: {loadError}. If this says a table does not exist, run migrations/002_core_network_tables.sql in Supabase.
          </div>
        </Card>
      )}

      <Card>
        <CardHeader title="Pipeline" hint={records.length + ' jurisdictions · ' + statesCovered + ' states'} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {STATUSES.map(s => (
            <div key={s.value} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Badge variant={s.variant}>{s.label}</Badge>
              <span style={{ fontSize: '13px', fontWeight: 600 }}>{counts[s.value] || 0}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <SectionLabel>ADD JURISDICTION</SectionLabel>
        <FormGrid>
          <Field label="Jurisdiction name"><Input value={form.name} onChange={set('name')} placeholder="e.g. Hillsborough County Fire Marshal" /></Field>
          <Field label="State"><Select value={form.state} onChange={set('state')} options={ALL_STATES} placeholder="Select state" /></Field>
          <Field label="County"><Input value={form.county} onChange={set('county')} /></Field>
          <Field label="Type"><Select value={form.jurisdiction_type} onChange={set('jurisdiction_type')} options={TYPES} /></Field>
          <Field label="Fire marshal / contact"><Input value={form.fire_marshal_name} onChange={set('fire_marshal_name')} /></Field>
          <Field label="Email"><Input type="email" value={form.contact_email} onChange={set('contact_email')} /></Field>
          <Field label="Phone"><Input value={form.contact_phone} onChange={set('contact_phone')} /></Field>
          <Field label="Current status"><Select value={form.requirement_status} onChange={set('requirement_status')} options={STATUSES} /></Field>
          <Field label="Source link (code / policy)"><Input value={form.source_url} onChange={set('source_url')} placeholder="https://…" /></Field>
          <Field label="Last verified"><Input type="date" value={form.last_verified} onChange={set('last_verified')} /></Field>
          <Field label="Notes" full><Textarea value={form.notes} onChange={set('notes')} placeholder="What they require today, who you spoke with, next step" /></Field>
        </FormGrid>
        <div className="form-actions">
          <SubmitBtn onClick={save} loading={loading}>Add jurisdiction</SubmitBtn>
        </div>
        <SaveMessage {...msg} />
      </Card>

      <Card>
        <CardHeader title="Tracker" hint={shown.length + ' shown'} />
        <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <div style={{ minWidth: '160px' }}>
            <Select value={filterState} onChange={e => setFilterState(e.target.value)} options={ALL_STATES} placeholder="All states" />
          </div>
          <div style={{ minWidth: '220px' }}>
            <Select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} options={STATUSES} placeholder="All statuses" />
          </div>
        </div>
        {!shown.length ? <EmptyState text="No jurisdictions match. Add your pilot jurisdictions above." /> : (
          shown.map(r => (
            <div key={r.id} className="list-row">
              <div className="list-row-info">
                <div className="list-row-name">{r.name} <span style={{ color: '#94A3B8', fontWeight: 400 }}>· {r.state}{r.county ? ' · ' + r.county : ''}</span></div>
                <div className="list-row-meta">
                  {[r.fire_marshal_name, r.contact_email, r.contact_phone].filter(Boolean).join(' · ') || 'No contact yet'}
                  {' · verified ' + fmtDate(r.last_verified)}
                </div>
                {r.notes && <div className="list-row-meta">{r.notes}</div>}
                {r.source_url && <div className="list-row-meta"><a href={r.source_url} target="_blank" rel="noopener noreferrer">Source</a></div>}
                <RecordMeta date={r.created_at} />
              </div>
              <div style={{ minWidth: '210px' }}>
                <Select value={r.requirement_status} onChange={e => updateStatus(r.id, e.target.value)} options={STATUSES} />
              </div>
            </div>
          ))
        )}
      </Card>
    </div>
  );
}
